﻿import { config } from '@/config/env';
import { showGlobalToast } from '@/components/common/Toast';

let refreshPromise: Promise<boolean> | null = null;

/**
 * Errore applicativo che porta con sé lo status HTTP e un messaggio leggibile.
 */
export class ApiError extends Error {
  status?: number;
  data?: unknown;
  constructor(message: string, status?: number, data?: unknown) {
    super(message || 'Errore imprevisto');
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Messaggio di fallback in base allo status HTTP.
 */
export function messaggioErroreDaStatus(status?: number): string {
  switch (status) {
    case 400: return 'Dati della richiesta non validi';
    case 401: return 'Sessione scaduta: effettua di nuovo l\'accesso';
    case 403: return 'Non hai i permessi per eseguire questa operazione';
    case 404: return 'Risorsa non trovata';
    case 409: return 'Conflitto: la versione potrebbe essere già stata modificata';
    case 500: return 'Errore interno del server';
    case 503: return 'Servizio temporaneamente non disponibile';
    default: return status ? ('Errore del server (HTTP ' + status + ')') : 'Errore di rete o server non raggiungibile';
  }
}

/**
 * Estrae un messaggio d'errore leggibile dal corpo di una risposta HTTP non-ok.
 * Gestisce i formati usati dal backend, ad es.:
 *   { "error": "{\"message\":\"EOF\"}", "errorcode": 400 }
 *   { "message": "..." }
 *   testo semplice
 * Se non trova nulla di utile, ripiega su un messaggio in base allo status.
 */
export async function extractApiErrorMessage(res: Response): Promise<string> {
  let raw = '';
  try {
    raw = await res.text();
  } catch {
    raw = '';
  }

  let messaggio = '';
  if (raw) {
    try {
      const data = JSON.parse(raw);
      // Alcuni endpoint annidano un JSON stringificato dentro "error"
      if (data && typeof data.error === 'string') {
        try {
          const inner = JSON.parse(data.error);
          messaggio = (inner && inner.message) ? inner.message : data.error;
        } catch {
          messaggio = data.error;
        }
      } else if (data && data.message) {
        messaggio = data.message;
      } else if (typeof data === 'string') {
        messaggio = data;
      }
    } catch {
      // Non era JSON: usa il testo così com'è
      messaggio = raw;
    }
  }

  if (!messaggio) {
    messaggio = messaggioErroreDaStatus(res.status);
  }
  return messaggio;
}

/**
 * Deriva un messaggio leggibile da un errore sollevato dal client API
 * (ApiError, oppure Error con eventuali .status/.data) o da un errore generico.
 */
export function messaggioDaErrore(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err && typeof err === 'object') {
    const e = err as { status?: number; data?: { error?: string; message?: string }; message?: string };
    // Prova a leggere il messaggio reale dal body già parsato dal client (.data)
    if (e.data) {
      const d = e.data;
      if (typeof d.error === 'string') {
        try {
          const inner = JSON.parse(d.error);
          if (inner && inner.message) return inner.message;
        } catch { /* ignore */ }
        return d.error;
      }
      if (typeof d.message === 'string' && d.message) return d.message;
    }
    if (typeof e.status === 'number') return messaggioErroreDaStatus(e.status);
    if (typeof e.message === 'string' && e.message) return e.message;
  }
  return messaggioErroreDaStatus(undefined);
}

class ApiClient {
  private baseURL: string;
  private cache = new Map<string, { data: unknown; timestamp: number }>();
  private cacheTime = 5 * 60 * 1000;
  private refreshThreshold = 5 * 60 * 1000; // 5 minutes before expiry

  constructor(baseURL?: string) {
    this.baseURL = baseURL || config.WSREST_TABATA;
  }

  /**
   * Get token, refreshing if needed (within 5 min of expiry)
   */
  private async getValidToken(): Promise<string | null> {
    const token = sessionStorage.getItem('mT');
    const exp = sessionStorage.getItem('mEx');
    const refreshToken = sessionStorage.getItem('mR');
    const userid = sessionStorage.getItem('mID');

    if (!token || !exp) return null;

    const expTime = Date.parse(exp);
    const needsRefresh = expTime - this.refreshThreshold < Date.now();

    if (!needsRefresh) return token;

    // Token is about to expire or expired - try refresh
    if (!refreshToken || !userid) {
      // No refresh token, session is dead
      return null;
    }

    // Prevent multiple simultaneous refresh calls
    if (!refreshPromise) {
      refreshPromise = this.refreshToken(userid, refreshToken)
        .finally(() => { refreshPromise = null; });
    }

    try {
      const ok = await refreshPromise;
      if (ok) return sessionStorage.getItem('mT');
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Refresh the access token
   */
  private async refreshToken(userid: string, refreshToken: string): Promise<boolean> {
    try {
      console.log('Refreshing token...');
      const base = this.baseURL.replace(/\/+$/, '');
      const url = base + '/refreshtoken/' + config.SITE + '/' + config.NOME_APP;

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userid, token: refreshToken }),
      });

      if (!res.ok) {
        console.error('Token refresh failed:', res.status);
        return false;
      }

      const data = await res.json();
      sessionStorage.setItem('mT', data.t);
      sessionStorage.setItem('mEx', data.tex);
      // Keep same refresh token
      sessionStorage.setItem('mR', refreshToken);
      console.log('Token refreshed successfully');
      return true;
    } catch (err) {
      console.error('Token refresh error:', err);
      return false;
    }
  }

  private buildURL(endpoint: string, params: Record<string, string | number> = {}): string {
    const base = this.baseURL.replace(/\/+$/, '');
    const path = endpoint.replace(/^\/+/, '');
    const fullPath = base + '/' + path;

    const qs = Object.entries(params)
      .filter(([, v]) => v !== null && v !== undefined)
      .map(([k, v]) => encodeURIComponent(k) + '=' + encodeURIComponent(String(v)))
      .join('&');

    return qs ? fullPath + '?' + qs : fullPath;
  }

  async request<T>(endpoint: string, options: {
    method?: string;
    body?: unknown;
    params?: Record<string, string | number>;
    requiresAuth?: boolean;
    cache?: boolean;
    signal?: AbortSignal;
  } = {}): Promise<T> {
    const { method = 'GET', body, params = {}, requiresAuth = true, cache = false, signal } = options;
    const url = this.buildURL(endpoint, params);

    if (method === 'GET' && cache) {
      const cached = this.cache.get(url);
      if (cached && Date.now() - cached.timestamp < this.cacheTime) return cached.data as T;
    }

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (requiresAuth) {
      const token = await this.getValidToken();
      if (!token) {
        this.handleSessionExpired();
        throw new Error('Authentication required');
      }
      headers['Authorization'] = 'Bearer ' + token;
    }

    const res = await fetch(url, {
      method,
      headers,
      body: body && method !== 'GET' ? JSON.stringify(body) : undefined,
      signal,
    });

    // Handle 401 - try refresh once, then fail
    if (res.status === 401) {
      const refreshToken = sessionStorage.getItem('mR');
      const userid = sessionStorage.getItem('mID');

      if (refreshToken && userid) {
        const refreshed = await this.refreshToken(userid, refreshToken);
        if (refreshed) {
          // Retry the request with new token
          const newToken = sessionStorage.getItem('mT');
          headers['Authorization'] = 'Bearer ' + newToken;
          const retryRes = await fetch(url, {
            method,
            headers,
            body: body && method !== 'GET' ? JSON.stringify(body) : undefined,
          });
          if (retryRes.status === 401) {
            this.handleSessionExpired();
            throw new Error('Session expired');
          }
          if (retryRes.status === 204) return null as T;
          if (!retryRes.ok) {
            const error: any = new Error('HTTP ' + retryRes.status);
            error.status = retryRes.status;
            throw error;
          }
          const data = await retryRes.json();
          if (method === 'GET' && cache) this.cache.set(url, { data, timestamp: Date.now() });
          return data as T;
        }
      }

      this.handleSessionExpired();
      throw new Error('Session expired');
    }

    if (res.status === 204) return null as T;
    if (!res.ok) {
      const error: any = new Error('HTTP ' + res.status + ': ' + res.statusText);
      error.status = res.status;
      try { error.data = await res.json(); } catch {}
      throw error;
    }

    const data = await res.json();
    if (method === 'GET' && cache) this.cache.set(url, { data, timestamp: Date.now() });
    return data as T;
  }

  private handleSessionExpired() {
    sessionStorage.clear();
    localStorage.removeItem('ruolo');
    this.cache.clear();

    if (!window.location.pathname.includes('/login')) {
      showGlobalToast('Sessione scaduta', 'Effettua nuovamente il login.', 'warning');
      setTimeout(() => { window.location.href = '/login'; }, 2000);
    }
  }

  get<T>(endpoint: string, opts: { params?: Record<string, string | number>; requiresAuth?: boolean; cache?: boolean; signal?: AbortSignal } = {}) { return this.request<T>(endpoint, { ...opts, method: 'GET' }); }
  post<T>(endpoint: string, body: unknown, opts: { params?: Record<string, string | number>; requiresAuth?: boolean; cache?: boolean; signal?: AbortSignal } = {}) { return this.request<T>(endpoint, { ...opts, method: 'POST', body }); }
  put<T>(endpoint: string, body: unknown, opts: { params?: Record<string, string | number>; requiresAuth?: boolean; cache?: boolean; signal?: AbortSignal } = {}) { return this.request<T>(endpoint, { ...opts, method: 'PUT', body }); }
  del<T>(endpoint: string, opts: { params?: Record<string, string | number>; requiresAuth?: boolean; cache?: boolean; signal?: AbortSignal } = {}) { return this.request<T>(endpoint, { ...opts, method: 'DELETE' }); }

  clearCache() { this.cache.clear(); }
}

export const api = new ApiClient();

